import {
	CORE_CONDITION_FIELD,
	type InquiryPackQuestionConfig,
	type InquiryProfileConfig,
} from "./inquiry-profile-schema";
import type { InquiryProfileAnswers, PackageFormData } from "./types";

/**
 * Which of a Profile's own questions (its Question Set, ADR-0022) are
 * asked right now, and which of the answers in the draft are answers to
 * them. One walk of the list, read by every surface that needs it: the
 * content step draws it, validation checks it, the estimate prices off it,
 * the summary panel shows it, and the submission sends it — so a question
 * the Customer cannot see is not required, drives nothing and never leaves
 * the browser, on every surface at once.
 *
 * A question is asked while every condition on it holds (`when`): a core
 * answer (`indoorOutdoor`), or the answer to a question above it on the
 * list — which must itself be asked. One flat list, in the Company's
 * order, any question able to wait on any earlier one. A question marked
 * `hiddenWhenTechRiderFile` is not asked while a document is attached,
 * because the parse answers it, and so neither is one waiting on it.
 *
 * `riderAttached` is required on every reader, never defaulted: the
 * content step once applied the document rule alone, so a required
 * question it hid still blocked validation with no field to fill.
 *
 * The draft itself is never pruned. An answer to a question no longer
 * asked stays in it, so Back and a changed mind are lossless, exactly as
 * a flat field's is; it is simply not asked, not checked, not shown and
 * not sent.
 */

/**
 * Whether one condition holds. Under a checkbox, "yes" holds while the
 * answer is exactly true and "no" while it is anything else: a form
 * checkbox has no separate no, so unticked is no. Under a select, the
 * answer must equal the option named. A question not asked, or not above
 * this one, holds nothing.
 */
export function conditionHolds(
	field: string,
	equals: string,
	formData: Partial<PackageFormData>,
	asked: ReadonlyMap<string, InquiryPackQuestionConfig>,
): boolean {
	if (field === CORE_CONDITION_FIELD) return formData[field] === equals;
	const source = asked.get(field);
	if (!source) return false;
	const value = (formData.profileAnswers ?? {})[field];
	if (source.type === "checkbox") {
		if (equals === "yes") return value === true;
		if (equals === "no") return value !== true;
		return false;
	}
	if (source.type === "select") {
		return typeof value === "string" && value === equals;
	}
	return false;
}

/** The questions asked right now, in the Company's order. */
export function activeQuestions(
	profile: InquiryProfileConfig,
	formData: Partial<PackageFormData>,
	riderAttached: boolean,
): InquiryPackQuestionConfig[] {
	const asked = new Map<string, InquiryPackQuestionConfig>();
	for (const question of profile.contentStep.packQuestions) {
		if (riderAttached && question.hiddenWhenTechRiderFile) continue;
		const holds = (question.when ?? []).every(({ field, equals }) =>
			conditionHolds(field, equals, formData, asked),
		);
		if (holds) asked.set(question.id, question);
	}
	return [...asked.values()];
}

/** Whether a question waits on the answer to another question. */
export function dependsOnQuestion(question: InquiryPackQuestionConfig): boolean {
	return (question.when ?? []).some(({ field }) => field !== CORE_CONDITION_FIELD);
}

/** Every declared question keyed by id. */
export function packQuestionsById(
	profile: InquiryProfileConfig,
): Map<string, InquiryPackQuestionConfig> {
	return new Map(
		profile.contentStep.packQuestions.map((question) => [question.id, question]),
	);
}

/** Whether a value is a well-formed answer to this kind of question. */
export function packAnswerSupported(
	question: InquiryPackQuestionConfig,
	value: unknown,
): boolean {
	if (question.type === "checkbox") return typeof value === "boolean";
	if (typeof value !== "string" || value === "") return false;
	if (question.type === "select") return question.options?.includes(value) ?? false;
	if (question.type === "integer") return /^\d+$/.test(value);
	return true;
}

/**
 * The answers that may leave the browser: only those to asked, declared
 * questions, with type-correct values (a select's answer must be one of
 * its options).
 */
export function activePackAnswers(
	profile: InquiryProfileConfig,
	formData: Partial<PackageFormData>,
	riderAttached: boolean,
): InquiryProfileAnswers {
	const result: InquiryProfileAnswers = {};
	const bag = formData.profileAnswers;
	if (!bag) return result;
	const asked = new Map(
		activeQuestions(profile, formData, riderAttached).map((question) => [
			question.id,
			question,
		]),
	);
	for (const [id, value] of Object.entries(bag)) {
		const question = asked.get(id);
		if (!question || !packAnswerSupported(question, value)) continue;
		result[id] = value;
	}
	return result;
}
